<template>
  <div class="site-page site-page--eighty nav-content-inset">
    <div class="site-page__inner">
      <h1 class="site-page__title">{{ (isAdmin || isSuperAdmin) ? '文章管理' : '我创建的文章' }}</h1>
      <p class="hint">
        {{ (isAdmin || isSuperAdmin)
          ? '管理全部文章。审核通过并发布后，才会出现在公开列表。'
          : '管理您创建的文章。发布后需管理员审核通过，才会出现在公开列表。' }}
      </p>

      <div class="toolbar">
        <el-button type="primary" @click="createNewBlog">写新文章</el-button>
      </div>

      <el-table :data="blogs" v-loading="loading" empty-text="暂无文章">
        <el-table-column label="封面" width="100">
          <template #default="s">
            <img v-if="s.row.banner" :src="s.row.banner" class="thumb" alt="" />
            <span v-else class="thumb thumb--empty">无图</span>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="名称" min-width="180" />
        <el-table-column label="分类" width="140">
          <template #default="s">{{ categoryText(s.row) }}</template>
        </el-table-column>
        <el-table-column v-if="!isAdmin" label="平台抽成" width="110">
          <template #default="s">{{ Number(s.row.platformCommissionPct) > 0 ? `${s.row.platformCommissionPct}%` : '15%（默认）' }}</template>
        </el-table-column>
        <el-table-column label="审核" width="110">
          <template #default="s">
            <el-tag :type="s.row.isApproved === 1 ? 'success' : 'warning'" size="small">
              {{ s.row.isApproved === 1 ? '已通过' : '待审核' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="发布" width="90">
          <template #default="s">
            <el-tag :type="s.row.isPublished ? 'success' : 'info'" size="small">
              {{ s.row.isPublished ? '已发布' : '草稿' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" min-width="320">
          <template #default="s">
            <el-button link type="primary" @click="viewBlog(s.row.id)">查看</el-button>
            <el-button link type="primary" @click="editBlog(s.row.id)">编辑</el-button>
            <el-button
              v-if="!s.row.isPublished"
              link
              type="success"
              @click="publishBlog(s.row.id)"
            >发布</el-button>
            <el-button
              v-else
              link
              type="warning"
              @click="unpublishBlog(s.row.id)"
            >取消发布</el-button>
            <el-button
              v-if="(isAdmin || isSuperAdmin) && s.row.authorId !== currentUserId"
              link
              type="warning"
              @click="adminEditBlog(s.row.id)"
            >管理员编辑</el-button>
            <el-button link type="danger" @click="deleteBlog(s.row.id)">删除</el-button>
            <el-tag v-if="s.row.onlyAdmin === 1" size="small" type="warning" class="only-admin">仅管理员</el-tag>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'
import { hasSysRole } from '@/utils/sysRole'
//import authManager from '@/utils/auth'

const router = useRouter()
const loading = ref(true)
const blogs = ref([])

// Role detection and state management
const isAdmin = ref(false)
const isSuperAdmin = ref(false)
const currentUserId = ref(null)

// Check user role and permissions
const checkUserRole = () => {
  try {
    const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}')
    const sysRole = userInfo.sysRole
    
    isAdmin.value = hasSysRole(sysRole, 'admin')
    isSuperAdmin.value = hasSysRole(sysRole, 'superadmin')
    currentUserId.value = userInfo.uuid
    
    console.log('User role detection:', {
      sysRole,
      isAdmin: isAdmin.value,
      isSuperAdmin: isSuperAdmin.value,
      currentUserId: currentUserId.value
    })
  } catch (error) {
    console.error('Error checking user role:', error)
    // Default to non-admin if role detection fails
    isAdmin.value = false
    isSuperAdmin.value = false
    currentUserId.value = null
  }
}

const fetchMyBlogs = async () => {
  try {
    loading.value = true
    const token = localStorage.getItem('jwt_token')
    
    // For admin/superadmin users, fetch all blogs; for regular users, fetch only their own
    const endpoint = (isAdmin.value || isSuperAdmin.value) ? '/api/v1/blog/list' : '/api/v1/blog/list/my'
    
    const response = await axios.post(endpoint, {}, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (response.data.code === 200) {
      blogs.value = response.data.data?.blogs || []
    } else {
      ElMessage.error(response.data.message || '获取文章列表失败')
    }
  } catch (error) {
    console.error('Error fetching blogs:', error)
    ElMessage.error('获取文章列表失败')
  } finally {
    loading.value = false
  }
}

const createNewBlog = () => {
  router.push('/blogs/create')
}

const viewBlog = (id) => {
  router.push(`/blogs/${id}`)
}

const editBlog = (id) => {
  router.push(`/blogs/edit/${id}`)
}

const publishBlog = async (id) => {
  try {
    const token = localStorage.getItem('jwt_token')
    const response = await axios.post('/api/v1/blog/publish', { id }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (response.data.code === 200) {
      ElMessage.success(response.data.message || '发布成功')
      await fetchMyBlogs()
    } else {
      ElMessage.error(response.data.message || '发布失败')
    }
  } catch (error) {
    console.error('Error publishing blog:', error)
    ElMessage.error('发布失败')
  }
}

const unpublishBlog = async (id) => {
  try {
    const token = localStorage.getItem('jwt_token')
    const response = await axios.post('/api/v1/blog/unpublish', { id }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (response.data.code === 200) {
      ElMessage.success('已取消发布')
      await fetchMyBlogs()
    } else {
      ElMessage.error(response.data.message || '操作失败')
    }
  } catch (error) {
    console.error('Error unpublishing blog:', error)
    ElMessage.error('操作失败')
  }
}

const deleteBlog = async (id) => {
  try {
    await ElMessageBox.confirm('确定要删除这篇文章吗？', '警告', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })

    const token = localStorage.getItem('jwt_token')
    const response = await axios.post('/api/v1/blog/delete', { id }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (response.data.code === 200) {
      ElMessage.success('删除成功')
      await fetchMyBlogs()
    } else {
      ElMessage.error(response.data.message || '删除失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('Error deleting blog:', error)
      ElMessage.error('删除失败')
    }
  }
}

// Admin edit function for navigation
const adminEditBlog = (id) => {
  router.push(`/blogs/edit/${id}`)
}

const categoryText = (blog) => {
  const cats = blog?.categories
  if (Array.isArray(cats) && cats.length) return cats.join('、')
  if (typeof cats === 'string' && cats) return cats
  return '—'
}

onMounted(() => {
  checkUserRole()
  fetchMyBlogs()
})
</script>

<style scoped>
.hint { color: #666; margin-bottom: 16px; }
.toolbar { margin-bottom: 12px; }
.thumb {
  width: 72px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  display: block;
  background: #f2f3f5;
}
.thumb--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #909399;
}
.only-admin { margin-left: 4px; }
</style>
